import { useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useMutation } from '@tanstack/react-query'
import { Loader2, Sparkles, KeyRound, CheckCircle, XCircle, Copy, X, Maximize2 } from 'lucide-react'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import api from '../lib/api'
import { toast } from '../lib/toast'
import { useAuthStore } from '../store/authStore'
import Dropdown from './Dropdown'
import AiKeyBanner from './AiKeyBanner'
import { useFocusTrap } from '../lib/useFocusTrap'

export type AiToolId = 'ai-citation' | 'geo-rewrite' | 'ai-answer'

const ENDPOINT: Record<AiToolId, string> = {
  'ai-citation': '/tools/ai-citation',
  'geo-rewrite': '/tools/geo-rewrite',
  'ai-answer': '/tools/ai-answer',
}

const PROVIDERS = [
  { id: 'gemini', label: 'Google Gemini' },
  { id: 'openai', label: 'OpenAI' },
  { id: 'groq', label: 'Groq' },
  { id: 'anthropic', label: 'Anthropic' },
  { id: 'mistral', label: 'Mistral AI' },
  { id: 'deepseek', label: 'DeepSeek' },
]

export default function AiToolPanel({ toolId }: { toolId: AiToolId }) {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuthStore()

  const [provider, setProvider] = useState('gemini')
  const [apiKey, setApiKey] = useState('')

  // tool-specific inputs
  const [brand, setBrand] = useState('')
  const [domain, setDomain] = useState('')
  const [prompts, setPrompts] = useState('')
  const [text, setText] = useState('')
  const [topic, setTopic] = useState('')
  const [url, setUrl] = useState('')
  const [question, setQuestion] = useState('')

  const run = useMutation({
    mutationFn: async () => {
      const payload: any = { ai_provider: provider }
      if (apiKey.trim()) payload.ai_key = apiKey.trim()
      if (toolId === 'ai-citation') { payload.brand = brand.trim(); payload.domain = domain.trim(); payload.prompts = prompts }
      if (toolId === 'geo-rewrite') { payload.text = text.trim(); payload.topic = topic.trim() }
      if (toolId === 'ai-answer') { payload.url = url.trim(); payload.question = question.trim() }
      const { data } = await api.post(ENDPOINT[toolId], payload)
      if (data?.error) throw Object.assign(new Error(data.message), { data })
      return data
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || err?.data?.message || err?.message || t('common.error', { defaultValue: 'Chyba' })
      toast.error(msg)
    },
  })

  const noKeyError = (run.error as any)?.response?.data?.no_ai_key || (run.error as any)?.data?.no_ai_key

  const canSubmit = (() => {
    if (!apiKey.trim() && !isAuthenticated) return false
    if (toolId === 'ai-citation') return (brand.trim() || domain.trim()) && prompts.trim()
    if (toolId === 'geo-rewrite') return text.trim().length >= 50
    if (toolId === 'ai-answer') return url.trim() && question.trim()
    return false
  })()

  return (
    <div className="space-y-4 mb-6">
      <AiKeyBanner />
      <form onSubmit={(e) => { e.preventDefault(); if (canSubmit) run.mutate() }}
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 space-y-4">

        {/* Tool-specific fields */}
        {toolId === 'ai-citation' && (
          <>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label={t('aitool.brand', { defaultValue: 'Název značky' })}>
                <input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="SEO Tools"
                  autoComplete="off" className={inp} />
              </Field>
              <Field label={t('aitool.domain', { defaultValue: 'Doména' })}>
                <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="example.com"
                  autoComplete="off" className={inp} />
              </Field>
            </div>
            <Field label={t('aitool.prompts', { defaultValue: 'Dotazy (každý na nový řádek)' })}
              hint={t('aitool.prompts_hint', { defaultValue: 'Co by zákazníci napsali do AI, max 10' })}>
              <textarea value={prompts} onChange={(e) => setPrompts(e.target.value)} rows={4}
                placeholder={'nejlepší SEO nástroj v ČR\njak zkontrolovat technické SEO'}
                autoComplete="off" className={clsx(inp, 'font-mono text-sm')} />
            </Field>
          </>
        )}

        {toolId === 'geo-rewrite' && (
          <>
            <Field label={t('aitool.topic', { defaultValue: 'Téma / klíčové slovo (nepovinné)' })}>
              <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="technické SEO"
                autoComplete="off" className={inp} />
            </Field>
            <Field label={t('aitool.text', { defaultValue: 'Text k přepsání' })}
              hint={t('aitool.text_hint', { defaultValue: 'Vložte obsah, který chcete připravit pro AI vyhledávače (min. 50 znaků)' })}>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8}
                placeholder={t('aitool.text_ph', { defaultValue: 'Vložte sem svůj text…' })}
                autoComplete="off" className={inp} />
            </Field>
          </>
        )}

        {toolId === 'ai-answer' && (
          <>
            <Field label={t('aitool.url', { defaultValue: 'URL stránky' })}>
              <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/clanek"
                autoComplete="off" className={inp} />
            </Field>
            <Field label={t('aitool.question', { defaultValue: 'Otázka uživatele' })}
              hint={t('aitool.question_hint', { defaultValue: 'Co by se uživatel zeptal AI o tématu té stránky' })}>
              <input value={question} onChange={(e) => setQuestion(e.target.value)}
                placeholder={t('aitool.question_ph', { defaultValue: 'Jak…?' })} className={inp} />
            </Field>
          </>
        )}

        {/* BYOK key */}
        <div className="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 p-4 bg-gray-50/60 dark:bg-gray-800/40">
          <div className="flex items-center gap-2 mb-3 text-sm font-medium text-gray-700 dark:text-gray-200">
            <KeyRound className="w-4 h-4 text-primary-500" />
            {t('aitool.key_title', { defaultValue: 'Vlastní AI klíč' })}
          </div>
          <div className="grid sm:grid-cols-[180px_1fr] gap-3">
            <Dropdown value={provider} onChange={setProvider}
              options={PROVIDERS.map((p) => ({ value: p.id, label: p.label }))} />
            <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
              name="byok-ai-key" autoComplete="new-password" data-lpignore="true" data-1p-ignore data-form-type="other"
              placeholder={isAuthenticated
                ? t('aitool.key_ph_auth', { defaultValue: 'Nepovinné — bez vyplnění se použije uložený klíč' })
                : t('aitool.key_ph', { defaultValue: 'Vložte svůj API klíč' })}
              className={clsx(inp, 'font-mono text-sm')} />
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {t('aitool.key_note', { defaultValue: 'Klíč se nikam neukládá, použije se jen pro tento dotaz. Náklad nese váš účet u providera.' })}
          </p>
        </div>

        <button type="submit" disabled={!canSubmit || run.isPending}
          className="bg-gradient-to-r from-primary-600 to-primary-500 text-white px-8 py-3 rounded-xl font-semibold hover:opacity-90 transition disabled:opacity-50 flex items-center gap-2">
          {run.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {t('aitool.run', { defaultValue: 'Spustit' })}
        </button>
      </form>

      {noKeyError && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-yellow-800">
          {(run.error as any)?.response?.data?.message || (run.error as any)?.data?.message}
        </div>
      )}

      {run.data && <AiToolResult toolId={toolId} data={run.data} />}
    </div>
  )
}

function AiToolResult({ toolId, data }: { toolId: AiToolId; data: any }) {
  const { t } = useTranslation()
  const [openAnswer, setOpenAnswer] = useState<any | null>(null)

  if (toolId === 'ai-citation') {
    const score = data.visibility_score ?? 0
    return (
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
        <div className="flex items-center gap-4 mb-5">
          <div className={clsx('text-4xl font-bold', score >= 50 ? 'text-green-600' : score > 0 ? 'text-yellow-600' : 'text-red-600')}>{score}%</div>
          <div className="text-sm text-gray-500">
            {t('aitool.cit_summary', { mentioned: data.mentioned, total: data.total, defaultValue: 'Značka zmíněna v {{mentioned}} z {{total}} odpovědí' })}
          </div>
        </div>
        <div className="space-y-2">
          {(data.results || []).map((r: any, i: number) => (
            <div key={i} className="border border-gray-100 dark:border-gray-800 rounded-xl p-3">
              <div className="flex items-start gap-2">
                {r.mentioned ? <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 text-gray-300 shrink-0 mt-0.5" />}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{r.prompt}</p>
                  {r.snippet && <p className="text-xs text-green-700 dark:text-green-400 mt-1">…{r.snippet}</p>}
                  {r.error && <p className="text-xs text-red-500 mt-1">{r.error}</p>}
                  <div className="flex items-center gap-3 mt-1.5">
                    {r.engine && <span className="text-[11px] text-gray-400">{r.engine}</span>}
                    {r.answer && (
                      <button
                        onClick={() => setOpenAnswer(r)}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-primary-600 hover:text-primary-700"
                      >
                        <Maximize2 className="w-3 h-3" /> {t('aitool.show_answer', { defaultValue: 'Zobrazit celou odpověď' })}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {openAnswer && <AiAnswerModal result={openAnswer} onClose={() => setOpenAnswer(null)} />}
      </div>
    )
  }

  // geo-rewrite & ai-answer — textový výstup
  const content = toolId === 'geo-rewrite' ? data.rewritten : data.answer
  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-gray-400">{data.engine}</span>
        <button onClick={() => { navigator.clipboard.writeText(content); toast.success(t('aitool.copied', { defaultValue: 'Zkopírováno' })) }}
          className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800">
          <Copy className="w-4 h-4" /> {t('aitool.copy', { defaultValue: 'Kopírovat' })}
        </button>
      </div>
      <pre className="text-sm bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-4 overflow-x-auto whitespace-pre-wrap break-words max-h-[32rem] text-gray-700 dark:text-gray-200">{content}</pre>
    </div>
  )
}

const inp = 'w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 dark:bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500'

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-xs text-gray-400 mt-1">{hint}</span>}
    </label>
  )
}

/** Popup s plnou odpovědí AI na jeden dotaz — ať uživatel vidí vše, co AI o webu vrátila. */
function AiAnswerModal({ result, onClose }: { result: any; onClose: () => void }) {
  const { t } = useTranslation()
  const answer = result.answer || result.answer_excerpt || ''
  const cardRef = useRef<HTMLDivElement>(null)
  useFocusTrap(cardRef, onClose)
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div aria-hidden="true" className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div ref={cardRef} className="relative w-full max-w-2xl max-h-[85vh] flex flex-col bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-white/10">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-100 dark:border-white/10">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{result.prompt}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">
              {result.engine}
              {' · '}
              {result.mentioned
                ? t('aitool.was_mentioned', { defaultValue: 'značka zmíněna' })
                : t('aitool.not_mentioned', { defaultValue: 'značka nezmíněna' })}
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={() => { navigator.clipboard.writeText(answer); toast.success(t('aitool.copied', { defaultValue: 'Zkopírováno' })) }}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <Copy className="w-3.5 h-3.5" /> {t('aitool.copy', { defaultValue: 'Kopírovat' })}
            </button>
            <button onClick={onClose} aria-label={t('common.close', { defaultValue: 'Zavřít' })} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="p-5 overflow-y-auto overscroll-contain">
          <p className="text-sm leading-relaxed whitespace-pre-wrap break-words text-gray-700 dark:text-gray-200">{answer}</p>
        </div>
      </div>
    </div>,
    document.body
  )
}
